﻿import Grid2 from "@mui/material/Grid2";
import Paper from "@mui/material/Paper";
import { createStyles, makeStyles } from "@mui/styles";
import type {
	ColDef,
	DefaultMenuItem,
	EditableCallbackParams,
	GetContextMenuItemsParams,
	ICellRendererParams,
	MenuItemDef,
	RowDoubleClickedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CustomLoadingOverlay from "../../../../components/loaders/LazyLoading/LazyLoading";
import { DeleteWorkOrderModal } from "../../../../shared/work-orders-modals/components/modal-delete-work-order";
import { EditWorkOrderModal } from "../../../../shared/work-orders-modals/components/modal-edit-work-order";
import { OpenWorkOrderModal } from "../../../../shared/work-orders-modals/components/modal-open-work-order";
import type { ModalWorkOrder } from "../../../../shared/work-orders-modals/models/ModalWorkOrder";
import { useAssetDetailsContext } from "../../context/assetDetailsContext";
import { AssetLinkRenderer } from "./components/assetLinkRenderer";
import { WorkOrderCommentsColumnColumn } from "./components/comments-buttons/comments-buttons.columns";
import { useLoadfilters } from "./hooks/useLoadFilters";
import { useSearchWorkOrderAttachments } from "@/shared/work-orders-modals/components/hooks/useSearchWOAttachaments";
import { dxtToLocalServerTime, ErrorModal } from "@dexteel/mesf-core";
import { CloseWorkOrderModalWithAttachments } from "@/shared/work-orders-modals/components/modal-close-work-order";
import { getCraftLetter } from "@/utils/craftLetter";
import { Button } from "@mui/material";
import FormatListBulletedSharpIcon from "@mui/icons-material/FormatListBulletedSharp";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";

const useStyles = makeStyles((theme) =>
	createStyles({
		paper: {
			padding: "10px 0",
			height: "100%",
			width: "100%",
		},
	}),
);

type onMenuOptionSelectedType =
	| "EDIT"
	| "EDIT-COMMENTS"
	| "SEE-COMMENTS"
	| "CLOSE-ORDER"
	| "ADD-ORDER"
	| "DELETE-ORDER"
	| "OPEN-ORDER";

type Props = {
	searchWorkOrders: Function;
	isLoading: boolean;
};

export default function WorkOrders({ searchWorkOrders, isLoading }: Props) {
	const classes = useStyles();

	const [isAttachmentsLoading, setIsAttachmentsLoading] =
		useState<boolean>(true);
	const [attachmentsError, setAttachmentsError] = useState<string>("");
	const {
		state: { assetSearchId, workOrdersRows, workOrderSelectedRow },
		actions: { setWorkOrderSelectedRow },
	} = useAssetDetailsContext();
	const [attachmentsList, setAttachmentsList] = useState([]);
	const searchWorkOrderAttachments = useSearchWorkOrderAttachments({
		WorkOrderId: workOrderSelectedRow.WorkOrderId,
		setAttachmentsList,
		setIsLoading: setIsAttachmentsLoading,
		setError: setAttachmentsError,
	});
	const [isCraftLoading, setIsCraftLoading] = useState(false);
	const [craftError, setCraftError] = useState("");
	const gridRef = useRef<AgGridReact>(null);
	const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
	const isMountRef = useRef(true);

	const [openWorkOrderModal, setOpenWorkOrderModal] = useState<boolean>(false);
	const [closeWorkOrderModal, setCloseWorkOrderModal] =
		useState<boolean>(false);
	const [openWorkOrderEditModal, setOpenWorkOrderEditModal] =
		useState<boolean>(false);
	const [openDeleteWorkOrderModal, setOpenDeleteWorkOrderModal] =
		useState<boolean>(false);
	const { loadFilterCrafts, loadFilterCloser } = useLoadfilters({
		setCraftError,
		setIsCraftLoading,
	});

	const loadingOverlayComponent = useMemo<any>(() => {
		return CustomLoadingOverlay;
	}, []);

	const renderCategoryCell = (props: ICellRendererParams) => {
		return (
			<div
				style={{
					height: 32,
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				<div
					style={{
						height: 26,
						width: 26,
						borderRadius: 4,
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						background: props.data?.CraftColor || "#FFFFFF",
					}}
				>
					<div style={{ color: "#FFF", fontWeight: 600 }}>
						<div>
							{getCraftLetter(props.data?.CraftName, props.data?.CraftCode)}
						</div>
					</div>
				</div>
			</div>
		);
	};

	const renderTypeCell = (props: ICellRendererParams) => {
		return (
			<div
				style={{
					height: 32,
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				<div
					style={{
						height: 26,
						width: 26,
						borderRadius: 4,
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						background: props.data?.TypeCode == "PM" ? "#00008B" : "#D3D3D3",
					}}
				>
					<div
						style={{
							color: props.data?.TypeCode == "PM" ? "#FFFFFF" : "#000000",
							fontWeight: 600,
						}}
					>
						<div>{props.data?.TypeCode || ""}</div>
					</div>
				</div>
			</div>
		);
	};

	const onActionsClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		params: EditableCallbackParams,
	) => {
		(params.api as any).showContextMenu({
			column: params.column,
			rowNode: params.node,
			undefined,
			mouseEvent: e.nativeEvent,
		});
	};

	const columnTypes = useMemo<{
		[key: string]: ColDef;
	}>(() => {
		return {
			CommentsColumn: {
				cellRenderer: (params: EditableCallbackParams) => {
					return (
						<WorkOrderCommentsColumnColumn
							searchWorkOrders={searchWorkOrders}
							params={params}
						/>
					);
				},
			},
		};
	}, []);

	const [columnDefs] = useState<ColDef[]>([
		{
			field: "WorkOrderName",
			headerName: "Name",
			minWidth: 70,
			maxWidth: 70,
			flex: 1,
		},
		{
			cellRenderer: AssetLinkRenderer,
			headerName: "Asset",
			minWidth: 500,
			maxWidth: 500,
			flex: 1,
			valueGetter: (params) => params.data.FullPath,
		},
		{ field: "Title", headerName: "Title", minWidth: 150, flex: 2 },
		{
			field: "StatusName",
			headerName: "Status",
			minWidth: 40,
			maxWidth: 50,
			flex: 1,
		},
		{
			field: "OpenTimestamp",
			headerName: "Open",
			minWidth: 120,
			maxWidth: 120,
			flex: 1,
			valueFormatter: (params) =>
				dxtToLocalServerTime(params.value, "MM/dd/yy HH:mm"),
		},
		{
			field: "CloseTimestamp",
			headerName: "Closed",
			minWidth: 130,
			maxWidth: 130,
			flex: 1,
			valueFormatter: (params) =>
				dxtToLocalServerTime(params.value, "MM/dd/yy HH:mm"),
		},
		{
			field: "ClosedBy",
			headerName: "Closed By",
			minWidth: 80,
			maxWidth: 80,
			flex: 1,
		},
		{
			field: "TypeCode",
			headerName: "Type",
			minWidth: 50,
			maxWidth: 50,
			flex: 1,
			cellRenderer: renderTypeCell,
		},
		{
			field: "CraftName",
			headerName: "Craft",
			minWidth: 40,
			maxWidth: 40,
			flex: 1,
			cellRenderer: renderCategoryCell,
		},
		{
			field: "DownHours",
			headerName: "Down Hours",
			minWidth: 50,
			maxWidth: 50,
			flex: 1,
		},
		{
			headerName: "Comments",
			type: "CommentsColumn",
			minWidth: 100,
			maxWidth: 100,
			flex: 1,
			cellClass: "ag-comments",
			wrapText: false,
			autoHeight: false,
		},
		{
			headerName: "",
			flex: 1,
			minWidth: 65,
			maxWidth: 65,
			cellClass: "ag-columns",
			wrapText: false,
			autoHeight: false,
			cellRenderer: (params: EditableCallbackParams) => {
				return (
					<Grid2
						container
						style={{
							height: "100%",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<Grid2
							style={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
							size={{ xs: 12, md: 12 }}
						>
							<Grid2
								container
								spacing={1}
								style={{ justifyContent: "flex-end", minWidth: 30 }}
							>
								<Grid2 size={{ xs: 12, md: 12 }}>
									<Button
										fullWidth
										style={{}}
										onClick={(e) => onActionsClick(e, params)}
									>
										<FormatListBulletedSharpIcon
											style={{ height: "auto" }}
											color="action"
										/>
									</Button>
								</Grid2>
							</Grid2>
						</Grid2>
					</Grid2>
				);
			},
		},
	]);

	const onMenuOptionSelected = (
		option: onMenuOptionSelectedType,
		row: ModalWorkOrder,
	) => {
		setWorkOrderSelectedRow(row);
		switch (option) {
			case "EDIT": {
				setOpenWorkOrderEditModal(true);
				return 0;
			}
			case "EDIT-COMMENTS": {
				setOpenWorkOrderEditModal(true);
				return 0;
			}
			case "CLOSE-ORDER": {
				setCloseWorkOrderModal(true);
				return 0;
			}
			case "OPEN-ORDER": {
				setOpenWorkOrderModal(true);
				return 0;
			}
			case "DELETE-ORDER": {
				setOpenDeleteWorkOrderModal(true);
				return 0;
			}
			default:
		}
	};

	const openModalCloseWorkOrder = (event: RowDoubleClickedEvent) => {
		setWorkOrderSelectedRow(event.node.data);
		setOpenWorkOrderEditModal(true);
	};

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const status = params.node?.data.StatusName;
			const result: (DefaultMenuItem | MenuItemDef)[] = [];

			if (workOrdersRows?.length >= 1 && params.node?.data) {
				result.push({
					name: "Edit Order",
					action: () => {
						onMenuOptionSelected("EDIT-COMMENTS", params.node?.data);
					},
					icon: '<i class="fas fa-edit"></i>',
				});
			}

			if (status === "Close") {
				result.push({
					name: "Open order",
					action: () => {
						onMenuOptionSelected("OPEN-ORDER", params.node?.data);
					},
					icon: '<i class="far fa-plus-square"></i>',
				});
			}
			if (status === "Open") {
				result.push({
					name: "Close order",
					action: () => {
						onMenuOptionSelected("CLOSE-ORDER", params.node?.data);
					},
					icon: '<i class="far fa-times-circle"></i>',
				});
			}
			if (workOrdersRows?.length >= 11) {
				result.push({
					name: "Delete",
					action: () => {
						onMenuOptionSelected("DELETE-ORDER", params.node?.data);
					},
					icon: '<i class="fas fa-trash"></i>',
				});
			}
			if (workOrdersRows?.length >= 1 && params.node?.data) {
				result.push("separator", "excelExport");
			} else result.push("excelExport");
			return result;
		},
		[workOrdersRows],
	);

	useEffect(() => {
		if (!isMountRef.current) {
			searchWorkOrders();
		}
		isMountRef.current = false;
	}, [assetSearchId]);

	const updateAttachmentsListFn = () => {
		searchWorkOrderAttachments();
	};

	useEffect(() => {
		if (openWorkOrderEditModal) searchWorkOrderAttachments();
	}, [openWorkOrderEditModal]);

	useEffect(loadFilterCloser, []);

	useEffect(() => {
		loadFilterCrafts();
	}, []);

	return (
		<Paper elevation={1} className={classes.paper}>
			<Grid2 container style={{ height: "100%" }}>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<div style={gridStyle}>
						<AgGridReact
							ref={gridRef}
							rowData={workOrdersRows}
							columnDefs={columnDefs}
							defaultColDef={globalDefaultColDef}
							columnTypes={columnTypes}
							rowHeight={32}
							headerHeight={32}
							animateRows={true}
							loadingOverlayComponent={loadingOverlayComponent}
							onRowDoubleClicked={openModalCloseWorkOrder}
							getContextMenuItems={getContextMenuItems}
							loading={isLoading}
						></AgGridReact>
					</div>
				</Grid2>
			</Grid2>
			<CloseWorkOrderModalWithAttachments
				show={closeWorkOrderModal}
				data={workOrderSelectedRow}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						searchWorkOrders();
						updateAttachmentsListFn();
					}
					setCloseWorkOrderModal(false);
				}}
			/>
			<OpenWorkOrderModal
				show={openWorkOrderModal}
				data={workOrderSelectedRow}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						searchWorkOrders();
					}
					setOpenWorkOrderModal(false);
				}}
			/>
			<DeleteWorkOrderModal
				show={openDeleteWorkOrderModal}
				data={workOrderSelectedRow}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						searchWorkOrders();
					}
					setOpenDeleteWorkOrderModal(false);
				}}
			/>
			<EditWorkOrderModal
				show={openWorkOrderEditModal}
				data={workOrderSelectedRow}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						searchWorkOrders();
					}
					setOpenWorkOrderEditModal(false);
				}}
			/>
			<ErrorModal error={craftError} onHide={() => setCraftError("")} />
			<ErrorModal
				error={attachmentsError}
				onHide={() => setAttachmentsError("")}
			/>
		</Paper>
	);
}
